import type { Dispatch } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import { agentsApi } from './agents.feature';
import { engageApi } from './engage.feature';
import { gtmAgentsApi } from './gtm-agents.feature';
import type { LearningDto, MemoryDto, MemoryItemDto } from '@/types/clu-memory';

/**
 * What an approved change or an undone save may have changed: any agent's
 * instructions, the skills list, and the writing settings a voice or proof
 * learning edits (the sequencing settings and the organization's profile).
 * Read again once the server says yes.
 */
async function refreshWhatALearningChanged(
  dispatch: Dispatch,
  queryFulfilled: Promise<unknown>
) {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  dispatch(
    gtmAgentsApi.util.invalidateTags([
      'GtmAgent',
      { type: 'Skill', id: 'LIST' },
    ])
  );
  dispatch(engageApi.util.invalidateTags(['EngageSequencingSettings']));
  dispatch(agentsApi.util.invalidateTags(['OrgProfile']));
}

/**
 * What Clu has learned: cleanse-api `/api/v1/memory`.
 * Callers skip every query while `useCluMemoryEnabled()` is off. Every action
 * refreshes the page, and refreshes it again when the server refuses (a 409
 * marks the learning stale there). An approval or an undo also refreshes the
 * agents, the skills, the sequencing settings and the organization's profile.
 */
export const cluMemoryApi = createApi({
  reducerPath: 'cluMemoryApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Memory'],
  endpoints: (builder) => ({
    getMemory: builder.query<MemoryDto, { orgId: string; userId: string }>({
      // The page is the organization's and the caller's own, personal notes
      // included: a switch of organization or of user never reads the last
      // one's cache (`userId` only keys it), and the server refuses a request
      // that raced a switch of organization.
      query: ({ orgId }) => ({
        url: '/memory',
        headers: { 'X-Cleanlist-Expected-Org': orgId },
      }),
      providesTags: ['Memory'],
    }),
    editMemoryNote: builder.mutation<
      MemoryItemDto,
      { id: string; content: string }
    >({
      query: ({ id, content }) => ({
        url: `/memory/notes/${id}`,
        method: 'PATCH',
        body: { content },
      }),
      invalidatesTags: ['Memory'],
    }),
    forgetMemoryNote: builder.mutation<void, string>({
      query: (id) => ({ url: `/memory/notes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Memory'],
    }),
    undoLearning: builder.mutation<LearningDto, string>({
      query: (id) => ({
        url: `/memory/learnings/${id}/undo`,
        method: 'POST',
      }),
      invalidatesTags: ['Memory'],
      onQueryStarted: (_id, { dispatch, queryFulfilled }) =>
        refreshWhatALearningChanged(dispatch, queryFulfilled),
    }),
    approveLearning: builder.mutation<LearningDto, string>({
      query: (id) => ({
        url: `/memory/learnings/${id}/approve`,
        method: 'POST',
      }),
      invalidatesTags: ['Memory'],
      onQueryStarted: (_id, { dispatch, queryFulfilled }) =>
        refreshWhatALearningChanged(dispatch, queryFulfilled),
    }),
    declineLearning: builder.mutation<LearningDto, string>({
      query: (id) => ({
        url: `/memory/learnings/${id}/decline`,
        method: 'POST',
      }),
      invalidatesTags: ['Memory'],
    }),
  }),
});

export const {
  useGetMemoryQuery,
  useEditMemoryNoteMutation,
  useForgetMemoryNoteMutation,
  useUndoLearningMutation,
  useApproveLearningMutation,
  useDeclineLearningMutation,
} = cluMemoryApi;
